<template>
  <div class="app-container finance-page">
    <finance-query-panel
      title="开票分析"
      description="历史系统导入数据按累计期初展示；月度趋势只统计财务在系统中正常登记的真实开票记录。"
      :report-year.sync="reportYear"
      :query-params.sync="queryParams"
      :dashboard="dashboard"
      :loading="loading"
      @query="queryData"
      @reset="resetQuery"
    />
    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="finance-alert"
      title="累计开票用于当前合同余额核对；历史导入日期不作为真实开票月份参与趋势分析。"
    />
    <el-row :gutter="14">
      <el-col :xs="24" :lg="16">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">{{ reportYear }}年正常业务开票趋势</div>
          <div v-if="!loading && hasBusinessInvoice" class="chart-wrap">
            <finance-chart :option="monthlyOption" />
          </div>
          <el-empty v-else-if="!loading" description="当前筛选范围暂无正常业务开票月度数据；历史期初不计入趋势" :image-size="90" />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="8">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">累计开票数据来源</div>
          <finance-chart v-if="!loading && hasInvoiceSource" :option="sourceOption" />
          <el-empty v-else-if="!loading" description="当前筛选范围暂无累计开票数据" :image-size="90" />
        </el-card>
      </el-col>
    </el-row>
    <el-row :gutter="14">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">部门累计开票</div>
          <finance-chart v-if="!loading && hasInvoiceSource" :option="departmentOption" />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">产品大类累计开票</div>
          <finance-chart v-if="!loading && hasInvoiceSource" :option="productOption" />
        </el-card>
      </el-col>
    </el-row>
    <el-card shadow="never" class="table-card">
      <div class="card-title">累计开票分布</div>
      <el-table :data="pagedInvoiceCumulativeRows" border stripe v-loading="loading">
        <el-table-column prop="contractYear" label="签约年份" width="100" />
        <el-table-column prop="departmentName" label="签约部门" min-width="150" />
        <el-table-column prop="productCategory" label="产品大类" min-width="160" />
        <el-table-column label="累计开票" width="160" align="right">
          <template slot-scope="scope">{{ amount(scope.row.totalAmount) }}</template>
        </el-table-column>
      </el-table>
      <pagination
        v-show="dashboard.invoiceCumulativeRows.length > 0"
        :total="dashboard.invoiceCumulativeRows.length"
        :page.sync="cumulativePageNum"
        :limit.sync="cumulativePageSize"
      />
    </el-card>
  </div>
</template>

<script>
import FinanceChart from './components/FinanceChart'
import FinanceQueryPanel from './components/FinanceQueryPanel'
import receivableFinanceMixin from './components/receivableFinanceMixin'
import { groupTotals, sumMonths } from './components/financeShared'

export default {
  name: 'InvoiceAnalysis',
  financeScene: 'invoice',
  components: { FinanceChart, FinanceQueryPanel },
  mixins: [receivableFinanceMixin],
  data () {
    return {
      cumulativePageNum: 1,
      cumulativePageSize: 20
    }
  },
  computed: {
    pagedInvoiceCumulativeRows () {
      const start = (this.cumulativePageNum - 1) * this.cumulativePageSize
      return (this.dashboard.invoiceCumulativeRows || []).slice(start, start + this.cumulativePageSize)
    },
    hasBusinessInvoice () {
      return Number((this.dashboard.summary || {}).reportYearBusinessInvoiceAmount || 0) !== 0
    },
    hasInvoiceSource () {
      return Number((this.dashboard.summary || {}).signedInvoiceAmount || 0) !== 0
    },
    monthlyOption () {
      const data = sumMonths(this.dashboard.businessInvoiceMonthlyRows)
      return {
        color: ['#7c6df2'], tooltip: { trigger: 'axis', valueFormatter: value => this.amount(value) },
        grid: { left: 24, right: 24, top: 24, bottom: 20, containLabel: true },
        xAxis: { type: 'category', data: Array.from({ length: 12 }, (item, index) => `${index + 1}月`) },
        yAxis: { type: 'value', axisLabel: { formatter: value => `${(value / 10000).toFixed(0)}万` } },
        series: [{ type: 'bar', barMaxWidth: 30, data }]
      }
    },
    sourceOption () {
      const summary = this.dashboard.summary || {}
      return {
        tooltip: { trigger: 'item', formatter: params => `${params.name}<br/>${this.amount(params.value)} 元<br/>${params.percent}%` },
        legend: { bottom: 0 },
        series: [{ type: 'pie', radius: ['42%', '68%'], center: ['50%', '44%'], data: [
          { name: '历史导入期初', value: Number(summary.historicalImportedInvoiceAmount || 0), itemStyle: { color: '#a0aec0' } },
          { name: '正常业务登记', value: Number(summary.businessInvoiceAmount || 0), itemStyle: { color: '#7c6df2' } }
        ].filter(item => item.value !== 0) }]
      }
    },
    departmentOption () {
      return this.barOption(groupTotals(this.dashboard.invoiceCumulativeRows, 'departmentName'))
    },
    productOption () {
      return this.barOption(groupTotals(this.dashboard.invoiceCumulativeRows, 'productCategory'))
    }
  },
  methods: {
    barOption (rows) {
      return {
        color: ['#7c6df2'], tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: value => this.amount(value) },
        grid: { left: 18, right: 24, top: 18, bottom: 16, containLabel: true },
        xAxis: { type: 'value', axisLabel: { formatter: value => `${(value / 10000).toFixed(0)}万` } },
        yAxis: { type: 'category', data: rows.map(item => item.name), axisLabel: { width: 100, overflow: 'truncate' } },
        series: [{ type: 'bar', barMaxWidth: 22, data: rows.map(item => item.value) }]
      }
    }
  },
  watch: {
    'dashboard.invoiceCumulativeRows' () {
      this.cumulativePageNum = 1
    }
  }
}
</script>

<style lang="scss" scoped>
.finance-page { min-height: calc(100vh - 84px); background: #f5f7fa; }
.chart-card, .table-card { margin-bottom: 14px; }
.finance-alert { margin-bottom: 14px; }
.card-title { margin-bottom: 12px; color: #303133; font-size: 16px; font-weight: 600; }
.chart-card { min-height: 390px; }
</style>
